import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import clsx from 'clsx'
import FallBackModal, { FALLBACK_TYPE } from 'components/portal/fall-back-modal'
import ResponsiveImage from 'components/images/responsive-image'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { isAppWebView, splitTextWith } from 'lib/utils/common/commonUtils'
import { WEB_VIEW_X_PLATFORM } from 'constants/cookies'
import env from 'config/env'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'

const STATIC_CONTENTS = {
  maskDonation: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/mask_donation_en.svg`,
      ratio: 0.1,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/mask_donation_en.svg`,
      ratio: 0.1,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/mask_donation_en.svg`,
      ratio: 0.1,
    },
  },
  masks: {
    src: `${env.IMG_HOST}img/pomelo-cares/masks.svg`,
    ratio: 0.923076923,
  },
  imagesBlock: {
    src: `${env.IMG_HOST}img/pomelo-cares/images_block.jpg`,
    ratio: 1,
  },
}

const formatNumber = (num) => {
  if (!num) {
    return num
  }

  return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

const PurpleSection = ({
  amount,
  country,
  isLottieReady,
  language,
  masks,
  t,
}) => {
  const globeHeartRef = useRef(null)
  const globeHeartAnimateRef = useRef(null)
  const [isWebview, setIsWebView] = useState(false)
  const [isShowMessage, setIsShowMessage] = useState(false)
  const [forceShowModal, setForceShowModal] = useState({})

  useEffect(() => {
    if (isLottieReady && !globeHeartAnimateRef.current) {
      globeHeartAnimateRef.current = window.bodymovin.loadAnimation({
        container: globeHeartRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: `${env.IMG_HOST}img/pomelo-cares/globe_heart.json`,
      })
    }
  }, [isLottieReady])

  useEffect(() => {
    if (isAppWebView()) {
      setIsWebView(true)
    }
  }, [])

  const textBody2 = splitTextWith(
    hasLocizeTranslation(
      t,
      'POMELO_CARE_PURPLE_BODY_2',
      '%b%Buy a Pomelo mask today.%b% All profits from mask packs purchased on Pomelo app or pomelofashion.com, will be donated to hospitals in your community to purchase medical equipment.',
    ),
    '%b%',
  )
  const textBody3 = splitTextWith(
    hasLocizeTranslation(
      t,
      'POMELO_CARE_PURPLE_BODY_3',
      '%b%Donate a pack of masks%b% to a worthy cause',
    ),
    '%b%',
  )

  const onWebViewLinkClick = (e) => {
    const isIos =
      !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
    const xPlatform = localStorage.getItem(WEB_VIEW_X_PLATFORM)
    let isOldIos = isIos

    if (xPlatform) {
      const [platform, version] = xPlatform.split(' ')

      if (platform === 'iOS' && version) {
        const [major, minor, patch] = version.split('.')

        // If it's the new version
        if (
          +major > 1 ||
          (+major >= 1 && +minor > 11) ||
          (+major >= 1 && +minor >= 11 && +patch >= 38)
        ) {
          isOldIos = false
        }
      }
    }

    /* Might be skip because it's a condition on iOS app-webview and can't mock for tested */
    /* istanbul ignore next */
    if (isOldIos) {
      e.preventDefault()
      setIsShowMessage(true)
      setForceShowModal({})
    }
  }

  return (
    <section className="pomelo-cares-purple">
      <ResponsiveImage
        className="mask-donation"
        src={STATIC_CONTENTS.maskDonation[language].src}
        imgRatio={STATIC_CONTENTS.maskDonation[language].ratio}
      />
      <span className={clsx(Typo.body2, 'text-mask-donation-body')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_PURPLE_BODY',
          'Surgical and non-medical protective masks are a valuable depleting resource needed by our hospitals, medical facilities and other essential frontline workers. At Pomelo, utilizing our production facilities and partners, we are committed to producing and donating masks to those hospitals in need.',
        )}
      </span>
      <ResponsiveImage
        className="masks"
        src={STATIC_CONTENTS.masks.src}
        imgRatio={STATIC_CONTENTS.masks.ratio}
      />
      <span className={clsx(Typo.h4, 'text-amount-value')}>
        {formatNumber(masks)}
      </span>
      <span className={clsx(Typo.caption, 'text-amount-label')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_PURPLE_PRODUCED',
          'No. of masks produced by pomelo',
        )}
      </span>
      <hr className="line-break" />
      <span className={clsx(Typo.caption, 'text-subtitle-1')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_PURPLE_SUBTITLE_1',
          'See how we’re doing so far:',
        )}
      </span>
      <div className="globe-heart" ref={globeHeartRef} />
      <span className={clsx(Typo.h4, 'text-amount-value')}>{amount}</span>
      <span className={clsx(Typo.caption, 'text-amount-label')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_PURPLE_AMOUNT_LABEL',
          `Money raised through
purchase of masks on pomelo`,
        )}
      </span>
      <span className={clsx(Typo.subtitle2, 'text-subtitle-2')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_PURPLE_SUBTITLE_2',
          'You can get involved too!',
        )}
      </span>
      <div className="text-body-2">
        {!!textBody2[0] && <span className={Typo.body2}>{textBody2[0]}</span>}
        {!!textBody2[1] && (
          <span className={Typo.subtitle2}>{textBody2[1]}</span>
        )}
        {!!textBody2[2] && <span className={Typo.body2}>{textBody2[2]}</span>}
      </div>
      <div className="text-body-3">
        {!!textBody3[0] && <span className={Typo.body2}>{textBody3[0]}</span>}
        {!!textBody3[1] && (
          <span className={Typo.subtitle2}>{textBody3[1]}</span>
        )}
        {!!textBody3[2] && <span className={Typo.body2}>{textBody3[2]}</span>}
      </div>
      {isWebview ? (
        <div className="cta-container">
          <Button
            link
            type={BUTTON_TYPES.outlinedLight}
            className="webview-cta link-btn"
            onClick={(e) => onWebViewLinkClick(e)}
            aProps={{ href: 'pomelofashion://deeplink/product/35399' }}
          >
            {hasLocizeTranslation(t, 'POMELO_CARE_PURPLE_LINK_1', 'shop masks')}
          </Button>
          <Button
            link
            type={BUTTON_TYPES.outlinedLight}
            className="webview-cta link-btn"
            onClick={(e) => onWebViewLinkClick(e)}
            aProps={{ href: 'pomelofashion://deeplink/product/35406' }}
          >
            {hasLocizeTranslation(
              t,
              'POMELO_CARE_PURPLE_LINK_2',
              'donate a mask',
            )}
          </Button>
          {isShowMessage && (
            <FallBackModal
              keyIndex={forceShowModal}
              modalType={FALLBACK_TYPE.close}
            >
              <div
                className={clsx(Typo.body2, 'pomelo-cares__update-app-notice')}
              >
                {hasLocizeTranslation(
                  t,
                  'POMELO_CARE_UPDATE_APP_NOTICE',
                  `Please update your application! So you can see our masks!`,
                )}
              </div>
            </FallBackModal>
          )}
        </div>
      ) : (
        <div className="cta-container">
          <Link
            href="/product?id=35399"
            as={`/${country}/${language}/Mask-apr/35399-reusable-face-mask-set-multi-color.html`}
          >
            <Button
              link
              className="webview-cta link-btn"
              type={BUTTON_TYPES.outlinedLight}
            >
              {hasLocizeTranslation(
                t,
                'POMELO_CARE_PURPLE_LINK_1',
                'shop masks',
              )}
            </Button>
          </Link>
          <Link
            href="/product?id=35406"
            as={`/${country}/${language}/mask-donation/35406-surgical-mask-white.html`}
          >
            <Button
              link
              className="webview-cta link-btn"
              type={BUTTON_TYPES.outlinedLight}
            >
              {hasLocizeTranslation(
                t,
                'POMELO_CARE_PURPLE_LINK_2',
                'donate a mask',
              )}
            </Button>
          </Link>
        </div>
      )}
    </section>
  )
}

PurpleSection.propTypes = {
  amount: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  isLottieReady: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  masks: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  t: PropTypes.func.isRequired,
}

export default PurpleSection
